// src/pages/AdminPromoModal.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  LayoutPanelTop,
  Users,
  Eye,
  Save,
  RotateCcw,
} from "lucide-react";
import PromoModal from "../components/PromoModal";

/** Brand */
const BG = "#0C0C1A";
const CARD = "#141428";
const BORDER = "#2A2A40";

/** API */
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window._API_BASE) ||
  "https://ecom-ads-ai-production.up.railway.app";

export default function AdminPromoModal() {
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [active, setActive] = useState(false);
  const [version, setVersion] = useState(1);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePublicId, setImagePublicId] = useState("");

  const [previewOpen, setPreviewOpen] = useState(false);

  const fileRef = useRef(null);

  const dirty = true;

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/promo-modal`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to load promo modal");

      const cfg = json?.config || {};
      setActive(!!cfg.active);
      setVersion(Number(cfg.version || 1) || 1);
      setTitle(String(cfg.title || ""));
      setDescription(String(cfg.description || ""));
      setImageUrl(String(cfg.imageUrl || ""));
      setImagePublicId(String(cfg.imagePublicId || ""));
    } catch (e) {
      setErr(e.message || "Failed to load promo modal");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/promo-modal`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          active,
          title,
          description,
          imageUrl,
          imagePublicId,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to save promo modal");

      const cfg = json?.config || {};
      setActive(!!cfg.active);
      setVersion(Number(cfg.version || 1) || 1);
      setTitle(String(cfg.title || ""));
      setDescription(String(cfg.description || ""));
      setImageUrl(String(cfg.imageUrl || ""));
      setImagePublicId(String(cfg.imagePublicId || ""));
    } catch (e) {
      setErr(e.message || "Failed to save promo modal");
    } finally {
      setSaving(false);
    }
  }, [getToken, active, title, description, imageUrl, imagePublicId]);

  const reshow = useCallback(async () => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/promo-modal/reshow`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const json = await res.json();
      if (!res.ok)
        throw new Error(json?.error || "Failed to re-show promo modal");
      const cfg = json?.config || {};
      setActive(!!cfg.active);
      setVersion(Number(cfg.version || 1) || 1);
    } catch (e) {
      setErr(e.message || "Failed to re-show promo modal");
    } finally {
      setSaving(false);
    }
  }, [getToken]);

  const uploadImage = useCallback(
    async (file) => {
      setSaving(true);
      setErr("");
      try {
        if (!file) return;

        if (file.size > 20 * 1024 * 1024) {
          throw new Error(
            "Image is too large. Please upload a file under 20MB."
          );
        }

        const token = await getToken();
        const form = new FormData();
        form.append("file", file);

        const res = await fetch(`${API_BASE}/api/admin/promo-modal/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || "Failed to upload image");

        setImageUrl(String(json.url || ""));
        setImagePublicId(String(json.public_id || ""));
      } catch (e) {
        setErr(e.message || "Failed to upload image");
      } finally {
        setSaving(false);
      }
    },
    [getToken]
  );

  return (
    <div
      className="min-h-screen w-full flex flex-col"
      style={{ background: BG }}
    >
      <div
        className="shrink-0 flex flex-col gap-3 px-4 py-3"
        style={{ background: BG, borderBottom: `1px solid ${BORDER}` }}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => navigate("/generate")}
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
              type="button"
            >
              <ArrowLeft size={16} />
              Dashboard
            </button>

            {/* ✅ Users + Landing buttons are present here */}
            <Link
              to="/admin/users"
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
              title="Users"
            >
              <Users size={16} />
              Users
            </Link>

            <Link
              to="/admin/landing"
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
              title="Landing"
            >
              <LayoutPanelTop size={16} />
              Landing
            </Link>

            <div className="flex items-baseline gap-3">
              <h1 className="text-base sm:text-lg font-semibold text-zinc-100">
                Admin · Promo Modal
              </h1>
              <span className="text-xs sm:text-sm text-zinc-400">
                version {version}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 text-sm"
              disabled={loading}
              type="button"
            >
              <Eye size={16} />
              Preview
            </button>

            <button
              onClick={reshow}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-700/50 text-amber-300 hover:border-amber-500 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={saving || loading}
              type="button"
              title="Show again to all users (even if they dismissed it before)"
            >
              <RotateCcw size={16} />
              Re-show
            </button>

            <button
              onClick={save}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={saving || loading || !dirty}
              type="button"
            >
              <Save size={16} />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 py-4">
        {err ? <div className="mb-3 text-sm text-amber-300">{err}</div> : null}

        <div
          className="rounded-xl p-4 border"
          style={{ background: CARD, borderColor: BORDER }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-zinc-100 font-semibold">Status</div>

            <label className="flex items-center gap-2 text-sm text-zinc-200 select-none">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="w-4 h-4 accent-pink-500"
              />
              Active (show after login)
            </label>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                placeholder="Promo title…"
              />
            </div>

            <div>
              <label className="block text-xs text-zinc-400 mb-1">
                Promo image
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadImage(f);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="px-3 py-2 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={saving}
                >
                  {saving ? "Uploading…" : "Upload image"}
                </button>
                {imageUrl ? (
                  <span className="text-xs text-zinc-400 truncate">
                    Image set
                  </span>
                ) : (
                  <span className="text-xs text-zinc-500">No image</span>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs text-zinc-400 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full min-h-[110px] px-3 py-2 rounded-lg bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                placeholder="Promo description…"
              />
            </div>

            <div className="md:col-span-2">
              <div className="text-xs text-zinc-500">
                Tip: click{" "}
                <span className="text-zinc-300">Re-show</span> to force the modal
                to appear again for everyone who dismissed it before.
              </div>
            </div>
          </div>

          {loading ? (
            <div className="mt-4 text-sm text-zinc-400">Loading…</div>
          ) : null}
        </div>
      </div>

      <PromoModal
        open={previewOpen}
        title={title}
        description={description}
        imageUrl={imageUrl}
        onClose={() => setPreviewOpen(false)}
        onPrimary={() => setPreviewOpen(false)}
        primaryLabel="Close preview"
      />
    </div>
  );
}
